import React, { useEffect, useMemo, useState } from 'react';
import {
  examAPI,
  TeacherExamAttemptResultItem,
  TeacherExamAttemptsResponse,
  TeacherExamItem,
  TeacherExamQuestionPerformanceItem,
  TeacherExamQuestionPerformanceResponse,
  TeacherExamsResponse,
} from '../services/api';
import T from '../theme/tokens';
import WorkspaceHeader from './WorkspaceHeader';
import {
  BANDS, BandKey, ExamRecord, attemptPct, bandMeta, bandOf, computeStats, normQuestionNo, questionCounts, recordAvg,
} from '../utils/examAnalytics';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, LabelList } from 'recharts';
import { BandGradients, bandFill, Card, ChartTooltip, InsightHero } from './AnalyticsKit';
import KaTeXText from './KaTeXText';
import { ExamInsightPanel, StudentFeedback, StudentTopicChart } from './ExamInsights';
import { Badge, Button, CountUp, EmptyState as UiEmptyState, IconBadge, Skeleton } from './ui';
import { chartCursorBar, seriesAnimation } from '../theme/chartTheme';
import '../styles/exam.css';

const FONT = T.font.sans;

interface ExamCorrectionPanelProps {
  data: TeacherExamsResponse | null;
  loading: boolean;
  /** Exams enriched with attempts + stats (from TeacherDashboard). */
  records?: ExamRecord[];
  /** When set, open this exam directly (e.g. jumped here from Analytics). */
  focusExamId?: number | null;
  onFocusHandled?: () => void;
}

const formatPercent = (value?: number | null) => {
  if (value == null || Number.isNaN(value)) return '—';
  return `${Number(value).toFixed(1)}%`;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const getAttemptScoreObtained = (item: TeacherExamAttemptResultItem) => item.total_marks_obtained ?? item.score_obtained ?? null;
const getAttemptMaxScore = (item: TeacherExamAttemptResultItem) => item.total_max_marks ?? item.max_score ?? null;
const getQuestionObtainedMarks = (item: TeacherExamQuestionPerformanceItem) => item.obtained_marks ?? item.total_score ?? null;

const getQuestionRows = (data: TeacherExamQuestionPerformanceResponse | null): TeacherExamQuestionPerformanceItem[] => {
  if (!data) return [];
  if ((data.items?.length ?? 0) > 0) return data.items ?? [];
  if ((data.questions_evaluation?.length ?? 0) > 0) return data.questions_evaluation ?? [];
  return [];
};

/* ─── Shared UI atoms ─────────────────────────────────── */

const HeroCard: React.FC<{
  pill?: string;
  title: React.ReactNode;
  subtitle: string;
  counterLabel?: string;
  counterValue?: string;
  counterColor?: string;
  showArt?: boolean;
  backOnClick?: () => void;
}> = ({ pill, title, subtitle, counterLabel, counterValue, backOnClick }) => (
  <WorkspaceHeader eyebrow={pill} title={title} description={subtitle} onBack={backOnClick}
    actions={counterLabel && counterValue != null ? (
      <div className="sl-header-count"><span>{counterLabel}</span><strong>{counterValue}</strong></div>
    ) : undefined}
  />
);


/* ─── Main component ──────────────────────────────────── */

const ExamCorrectionPanel: React.FC<ExamCorrectionPanelProps> = ({ data, loading, records = [], focusExamId, onFocusHandled }) => {
  const [selectedExam, setSelectedExam] = useState<TeacherExamItem | null>(null);
  const [examAttemptsData, setExamAttemptsData] = useState<TeacherExamAttemptsResponse | null>(null);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<TeacherExamAttemptResultItem | null>(null);
  const [questionPerformanceData, setQuestionPerformanceData] = useState<TeacherExamQuestionPerformanceResponse | null>(null);
  const [questionLoading, setQuestionLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'high-score' | 'low-score' | 'most-students' | 'attention'>('newest');
  const [classFilter, setClassFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [segment, setSegment] = useState<'all' | BandKey>('all');
  const [showCorrect, setShowCorrect] = useState(false);

  const examTime = (e: TeacherExamItem) => e.processed_at ?? e.created_at ?? '';

  const recordById = useMemo(() => new Map(records.map((r) => [r.id, r])), [records]);
  const isPending = (e: TeacherExamItem) => recordById.get(e.exam_id)?.pending ?? !(Number(e.total_students ?? 0) > 0);
  const passRateOf = (e: TeacherExamItem) => recordById.get(e.exam_id)?.stats?.passRate ?? null;

  const classOptions = useMemo(() => {
    const m = new Map<string, string>();
    records.forEach((r) => { if (!r.pending) m.set(r.classKey, r.classLabel); });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1], undefined, { numeric: true }));
  }, [records]);
  const subjectOptions = useMemo(() => {
    const m = new Map<string, string>();
    records.forEach((r) => { if (!r.pending) m.set(r.subjectKey, r.subjectLabel); });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [records]);

  const filteredExams = useMemo(() => {
    const list = [...(data?.items ?? [])].filter((e) => {
      const rec = recordById.get(e.exam_id);
      if (classFilter !== 'all' && rec?.classKey !== classFilter) return false;
      if (subjectFilter !== 'all' && rec?.subjectKey !== subjectFilter) return false;
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return (e.name || e.exam_name || '').toLowerCase().includes(q);
    });

    const avgOf = (e: TeacherExamItem) => {
      const rec = recordById.get(e.exam_id);
      if (rec) return recordAvg(rec);
      return isPending(e) || e.average_score == null ? null : Number(e.average_score);
    };
    let sorted: TeacherExamItem[];
    switch (sortBy) {
      case 'oldest': sorted = list.sort((a, b) => examTime(a).localeCompare(examTime(b))); break;
      case 'high-score': sorted = list.sort((a, b) => (avgOf(b) ?? -1) - (avgOf(a) ?? -1)); break;
      case 'low-score': sorted = list.sort((a, b) => (avgOf(a) ?? 101) - (avgOf(b) ?? 101)); break;
      case 'most-students': sorted = list.sort((a, b) => (Number(b.total_students ?? 0)) - (Number(a.total_students ?? 0))); break;
      case 'attention': sorted = list.sort((a, b) => (passRateOf(a) ?? 101) - (passRateOf(b) ?? 101)); break;
      case 'newest':
      default: sorted = list.sort((a, b) => examTime(b).localeCompare(examTime(a)));
    }
    // Exams still awaiting results always sink to the bottom
    return [...sorted.filter((e) => !isPending(e)), ...sorted.filter((e) => isPending(e))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, query, sortBy, classFilter, subjectFilter, recordById]);

  const exams = useMemo(() => [...(data?.items ?? [])], [data]);

  const questionRows = useMemo(() => getQuestionRows(questionPerformanceData), [questionPerformanceData]);

  /* Exam-level derived data */
  const currentRecord = selectedExam ? recordById.get(selectedExam.exam_id) : undefined;
  const currentAttempts = useMemo(() => examAttemptsData?.items ?? [], [examAttemptsData]);
  const liveRecord: ExamRecord | undefined = useMemo(() => {
    if (!currentRecord) return undefined;
    if (currentRecord.stats && currentRecord.stats.n > 0) return currentRecord;
    return currentAttempts.length ? { ...currentRecord, attempts: currentAttempts, stats: computeStats(currentAttempts), pending: false } : currentRecord;
  }, [currentRecord, currentAttempts]);
  const commonMax = liveRecord?.stats?.commonMax ?? null;

  useEffect(() => { setSegment('all'); }, [selectedExam]);
  useEffect(() => { setShowCorrect(false); }, [selectedStudent]);

  /* Jump straight to an exam when asked (from Analytics) */
  useEffect(() => {
    if (focusExamId == null || !data) return;
    const exam = (data?.items ?? []).find((e) => e.exam_id === focusExamId);
    if (exam) openExamAnalytics(exam);
    onFocusHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusExamId, data]);

  const openExamAnalytics = async (exam: TeacherExamItem) => {
    setSelectedExam(exam);
    setSelectedStudent(null);
    setQuestionPerformanceData(null);
    setExamAttemptsData(null);
    try {
      setAttemptsLoading(true);
      const response = await examAPI.getTeacherExamAttempts(exam.exam_id, 1000);
      setExamAttemptsData(response);
    } catch (error) {
      console.error('Failed to load exam analytics:', error);
      setExamAttemptsData({
        exam_id: exam.exam_id,
        exam_name: exam.name || exam.exam_name || `Exam #${exam.exam_id}`,
        exam_type: exam.exam_type,
        teacher_id: exam.teacher_id ?? 0,
        teacher_username: exam.teacher_username ?? '',
        teacher_name: exam.teacher_name ?? '',
        school_id: data?.school_id ?? 0,
        school_name: data?.school_name ?? '',
        school_code: data?.school_code ?? null,
        class_name: null, section_name: null,
        total_students: exam.total_students,
        average_score: exam.average_score,
        items: [],
      });
    } finally {
      setAttemptsLoading(false);
    }
  };

  const openStudentAnalytics = async (student: TeacherExamAttemptResultItem) => {
    if (!selectedExam) return;
    setSelectedStudent(student);
    setQuestionPerformanceData(null);
    try {
      setQuestionLoading(true);
      const response = await examAPI.getTeacherExamQuestionPerformance(selectedExam.exam_id, student.student_id, 200);
      setQuestionPerformanceData(response);
    } catch (error) {
      console.error('Failed to load question analytics:', error);
      setQuestionPerformanceData({
        exam_id: selectedExam.exam_id,
        exam_name: selectedExam.name || selectedExam.exam_name || `Exam #${selectedExam.exam_id}`,
        student_result_id: student.student_result_id, student_id: student.student_id,
        student_name: student.student_name, username: student.username,
        roll_number: student.roll_number ?? null,
        teacher_id: selectedExam.teacher_id ?? 0,
        teacher_username: selectedExam.teacher_username ?? '',
        teacher_name: selectedExam.teacher_name ?? '',
        school_id: data?.school_id ?? 0,
        school_name: data?.school_name ?? '',
        school_code: data?.school_code ?? null,
        class_name: student.class_name, section_name: student.section_name,
        total_questions: 0, items: [],
      });
    } finally {
      setQuestionLoading(false);
    }
  };

  const resetToExamList = () => {
    setSelectedExam(null); setExamAttemptsData(null);
    setSelectedStudent(null); setQuestionPerformanceData(null);
  };
  const resetToStudentList = () => {
    setSelectedStudent(null); setQuestionPerformanceData(null);
  };

  /* ─── Loading state ──────────────────────────────── */
  if (loading) {
    return (
      <div style={{ display: 'grid', gap: 20, fontFamily: FONT }} aria-busy="true">
        <Skeleton height={150} radius={20} />
        <Skeleton height={62} radius={16} />
        <div className="sl-ex-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="sl-ex-card" style={{ padding: 20, gap: 14 }}>
              <div style={{ display: 'flex', gap: 14 }}>
                <Skeleton height={40} width={40} radius={10} />
                <div style={{ flex: 1, display: 'grid', gap: 8 }}><Skeleton height={16} width="60%" /><Skeleton height={12} width="40%" /></div>
              </div>
              <div className="sl-ex-tiles"><Skeleton height={86} radius={12} /><Skeleton height={86} radius={12} /></div>
              <Skeleton height={120} radius={12} />
              <Skeleton height={44} radius={12} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ─── Question-level (student drill-in) view ────── */
  if (selectedExam && selectedStudent) {
    const examName = selectedExam.name || selectedExam.exam_name || `Exam #${selectedExam.exam_id}`;
    return (
      <div style={{ display: "grid", gap: 20, fontFamily: FONT }}>
        <InsightHero
          eyebrow="Student analytics"
          title={selectedStudent.student_name}
          insight={`Question-level performance for ${examName}. Review each question's marks, percentage, and gap analysis.`}
          onBack={resetToStudentList}
          chips={[
            examName,
            ...(selectedStudent.class_name ? [`Class ${selectedStudent.class_name}`] : []),
            ...(selectedStudent.section_name ? [`Sec ${selectedStudent.section_name}`] : []),
            ...(selectedStudent.roll_number != null ? [`Roll ${selectedStudent.roll_number}`] : []),
          ]}
          leading={
            <>
              <Button variant="glass" onClick={resetToExamList}
                icon={<svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" /></svg>}>
                All Exams
              </Button>
              {/* Marks and Grade sit in the title row's right corner as compact tiles */}
              <div className="sl-header-count sl-header-count--compact"><span>Marks</span><strong>{getAttemptScoreObtained(selectedStudent) ?? "—"} / {getAttemptMaxScore(selectedStudent) ?? "—"}</strong></div>
              <div className="sl-header-count sl-header-count--compact"><span>Grade</span><strong>{String(selectedStudent.grade || "—")}</strong></div>
            </>
          }
        />

        {questionLoading ? (
          <CardShell>
            <div role="status" aria-label="Loading question analytics…" style={{ display: 'grid', gap: 12 }}>
              <Skeleton height={18} width="30%" />
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={22} width={`${90 - i * 12}%`} radius={999} />)}
              <div style={{ textAlign: 'center', paddingTop: 6, color: T.text.tertiary, fontSize: 13 }}>Loading question analytics…</div>
            </div>
          </CardShell>
        ) : questionRows.length === 0 ? (
          <CardShell><EmptyState title="No question-wise analytics" body="Detailed question analytics for this student haven't been processed yet." /></CardShell>
        ) : (() => {
          const isFull = (q: TeacherExamQuestionPerformanceItem) => {
            const max = Number(q.max_marks ?? 0);
            const got = Number(getQuestionObtainedMarks(q) ?? 0);
            return max > 0 ? got >= max : (q.percentage ?? 0) >= 100;
          };
          const countedRows = questionRows.filter(questionCounts);
          const gaps = [...countedRows.filter((q) => !isFull(q))].sort((a, b) => (a.percentage ?? 0) - (b.percentage ?? 0));
          // Full-mark questions, then optional questions the student didn't choose
          const correct = [...countedRows.filter(isFull), ...questionRows.filter((q) => !questionCounts(q))];
          const unchosen = questionRows.length - countedRows.length;
          const visibleRows = showCorrect ? [...gaps, ...correct] : gaps;
          return (
          <>
          <StudentTopicChart questionRows={questionRows} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text.primary }}>
              Question analysis <span style={{ color: T.text.tertiary, fontWeight: 600, fontSize: 13 }}>· {gaps.length} question{gaps.length === 1 ? '' : 's'} lost marks, weakest first</span>
            </div>
          </div>
          <ResultsTable columns={['Question', 'Marks', 'Obtained', 'Percentage', 'Gap Analysis']}>
            {visibleRows.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '24px 16px', color: T.color.success.text, fontWeight: 700 }}>
                ✓ Every question answered with full marks — no gaps.
              </td></tr>
            )}
            {visibleRows.map((item, index) => {
              const p = item.percentage;
              const tone: 'success' | 'warning' | 'danger' | 'neutral' = p == null ? 'neutral' : p >= 75 ? 'success' : p >= 50 ? 'warning' : 'danger';
              const c = p == null ? T.text.tertiary : p >= 75 ? T.color.success.solid : p >= 50 ? T.color.warning.solid : T.color.danger.solid;
              const textColor = p == null ? T.text.tertiary : p >= 75 ? T.color.success.text : p >= 50 ? T.color.warning.text : T.color.danger.text;
              const max = Number(item.max_marks ?? 0);
              const obtained = Number(getQuestionObtainedMarks(item) ?? 0);
              return (
                <tr key={String(item.id ?? item.question_number ?? index)}>
                  <td style={{ width: 90 }}>
                    <span className="sl-ex-qtag">Q{normQuestionNo(item.question_number)}</span>
                  </td>
                  <td>
                    <span className="sl-num" style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>
                      {item.max_marks}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span className="sl-num" style={{ fontSize: 15, fontWeight: 800, color: textColor }}>
                        {getQuestionObtainedMarks(item) ?? '—'}
                      </span>
                      {max > 0 && <span style={{ fontSize: 11.5, color: T.text.tertiary, fontWeight: 600 }}>/ {max}</span>}
                    </div>
                  </td>
                  <td>
                    <div className="sl-ex-meter">
                      <div className="sl-ex-meter__track">
                        <div className="sl-ex-meter__fill" style={{
                          width: `${Math.max(0, Math.min(100, p ?? 0))}%`,
                          background: `linear-gradient(90deg, ${c}B3, ${c})`,
                        }} />
                      </div>
                      <Badge tone={tone} className="sl-ex-pct">{formatPercent(p)}</Badge>
                    </div>
                  </td>
                  <td className="sl-ex-gap">
                    {(item.error_type || (item.concepts_required?.length ?? 0) > 0) && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                        {item.error_type && (
                          <Badge tone="danger" style={{ height: 22, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {String(item.error_type).replace(/_/g, ' ')}
                          </Badge>
                        )}
                        {(item.concepts_required ?? []).slice(0, 3).map((c, ci) => {
                          const label = typeof c === 'string' ? c : (c?.concept_name ?? (c as any)?.name ?? '');
                          return label ? (
                            <span key={ci} className="sl-badge sl-badge--brand sl-rich" style={{ height: 'auto', minHeight: 22, padding: '2px 9px', fontSize: 11, whiteSpace: 'normal' }}><KaTeXText text={label} /></span>
                          ) : null;
                        })}
                      </div>
                    )}
                    {!questionCounts(item)
                      ? <span style={{ color: T.text.tertiary, fontStyle: 'italic' }}>Optional question — not chosen, not counted</span>
                      : item.gap_analysis
                        ? <span className="sl-rich" style={{ display: 'block' }}><KaTeXText text={item.gap_analysis} /></span>
                        : <span style={{ color: T.text.tertiary, fontStyle: 'italic' }}>{isFull(item) ? 'Full marks' : 'No gap identified'}</span>}
                  </td>
                </tr>
              );
            })}
            {correct.length > 0 && (
              <tr className="sl-ex-full-row">
                <td colSpan={5}>
                  <button className="sl-ex-linkbtn" onClick={() => setShowCorrect((v) => !v)} aria-expanded={showCorrect}>
                    ✓ {correct.length - unchosen} question{correct.length - unchosen === 1 ? '' : 's'} with full marks
                    {unchosen > 0 && <span style={{ color: T.text.secondary, fontWeight: 600 }}>· {unchosen} optional not chosen</span>}
                    <span style={{ color: T.text.tertiary, fontWeight: 600 }}>· {showCorrect ? 'Hide' : 'Show'}</span>
                  </button>
                </td>
              </tr>
            )}
          </ResultsTable>
          <StudentFeedback attempt={selectedStudent} />
          </>
          );
        })()}
      </div>
    );
  }

  /* ─── Exam-level (students list) view ──────────── */
  if (selectedExam) {
    const attempts = examAttemptsData?.items ?? [];
    const examName = selectedExam.name || selectedExam.exam_name || `Exam #${selectedExam.exam_id}`;
    return (
      <div style={{ display: "grid", gap: 20, fontFamily: FONT }}>
        <InsightHero
          eyebrow="Exam results"
          title={examName}
          insight={`${liveRecord ? `${liveRecord.classLabel} · ${liveRecord.subjectLabel} · ` : ""}${(selectedExam.exam_type || "Exam")} · ${attempts.length || selectedExam.total_students || 0} student${attempts.length === 1 ? "" : "s"} · Processed ${formatDateTime(selectedExam.processed_at)}`}
          onBack={resetToExamList}
        />

        {attemptsLoading ? (
          <CardShell>
            <div role="status" aria-label="Loading student results…" style={{ display: 'grid', gap: 12 }}>
              <Skeleton height={18} width="28%" />
              <Skeleton height={220} radius={12} />
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={44} radius={10} />)}
              <div style={{ textAlign: 'center', paddingTop: 6, color: T.text.tertiary, fontSize: 13 }}>Loading student results…</div>
            </div>
          </CardShell>
        ) : attempts.length === 0 ? (
          <CardShell><EmptyState title="No student results yet" body="Once students submit this exam, their scores will appear here." /></CardShell>
        ) : (() => {
          const ranked = [...attempts]
            .sort((a, b) => {
              const pa = attemptPct(a);
              const pb = attemptPct(b);
              // Nulls sink to the bottom
              if (pa == null && pb == null) return 0;
              if (pa == null) return 1;
              if (pb == null) return -1;
              return pb - pa;
            })
            .map((item, index) => ({ item, rank: index + 1 }));
          const segCounts = BANDS.reduce((acc, bd) => {
            acc[bd.key] = ranked.filter(({ item }) => { const p = attemptPct(item); return p != null && bandOf(p) === bd.key; }).length;
            return acc;
          }, {} as Record<BandKey, number>);
          const visible = segment === 'all' ? ranked : ranked.filter(({ item }) => { const p = attemptPct(item); return p != null && bandOf(p) === segment; });
          const MEDALS = ['linear-gradient(135deg, #FDE68A, #F59E0B)', 'linear-gradient(135deg, #F1F5F9, #94A3B8)', 'linear-gradient(135deg, #FED7AA, #EA580C)'];
          return (
          <>
          <ExamInsightPanel record={liveRecord} attempts={attempts} />

          <Card padding={0} className="sl-ex-table-card">
            <div style={{ padding: '18px 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <h3 className="sl-card__title">Student results</h3>
              <div className="sl-seg sl-seg--sm sl-ex-seg" role="tablist" aria-label="Filter students by score band">
                {[{ key: 'all' as const, label: 'All', count: ranked.length, color: T.color.brand[600] }, ...BANDS.map((bd) => ({ key: bd.key, label: bd.label, count: segCounts[bd.key], color: bd.color }))].map((t) => {
                  const active = segment === t.key;
                  return (
                    <button key={t.key} type="button" role="tab" aria-selected={active}
                      className={`sl-seg__item${active ? ' is-active' : ''}`} onClick={() => setSegment(t.key)}>
                      {t.key !== 'all' && <span className="sl-ex-seg__swatch" style={{ background: t.color }} aria-hidden />}
                      {t.label}
                      <span className="sl-ex-seg__count">{t.count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="sl-table-wrap">
              <table className="sl-table">
                <thead>
                  <tr>
                    {['Rank', 'Student', 'Class · Section', 'Marks', 'Percentage', 'Grade', ''].map((h, i) => (
                      <th key={`${h}${i}`} style={{ textAlign: i === 6 ? 'right' : 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '24px 16px', color: T.text.tertiary }}>No students in this band.</td></tr>
                  )}
                  {visible.map(({ item, rank }) => {
                    const p = attemptPct(item);
                    const band = p == null ? null : bandMeta(bandOf(p));
                    const itemMax = getAttemptMaxScore(item);
                    const differentPaper = commonMax != null && itemMax != null && Number(itemMax) !== Number(commonMax);
                    return (
                      <tr key={item.student_result_id} className={rank <= 3 ? 'is-podium' : undefined}>
                        <td style={{ width: 56 }}>
                          {rank <= 3 ? (
                            <span className="sl-ex-rank" style={{ background: MEDALS[rank - 1], color: rank === 2 ? '#1F2937' : rank === 1 ? '#78350F' : '#FFFFFF', boxShadow: '0 4px 10px -4px rgba(0,0,0,0.35)' }}>{rank}</span>
                          ) : (
                            <span className="sl-ex-rank sl-ex-rank--plain">{rank}</span>
                          )}
                        </td>
                        <td>
                          <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary, letterSpacing: '-0.01em' }}>{item.student_name}</div>
                          {item.roll_number != null && (
                            <div style={{ fontSize: 11, color: T.text.tertiary, marginTop: 2, fontWeight: 600, letterSpacing: '0.02em' }}>ROLL · {item.roll_number}</div>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                            <Badge tone="brand">{item.class_name || '—'}</Badge>
                            <Badge>Sec {item.section_name || '—'}</Badge>
                          </div>
                        </td>
                        <td>
                          <span className="sl-num" style={{ fontSize: 14, fontWeight: 800, color: T.text.primary }}>{getAttemptScoreObtained(item) ?? '—'}</span>
                          <span style={{ fontSize: 11.5, color: T.text.tertiary, fontWeight: 600 }}> / {itemMax ?? '—'}</span>
                          {differentPaper && (
                            <div title={`Most students were marked out of ${commonMax}. Check that this answer sheet was fully evaluated.`} style={{ marginTop: 4, cursor: 'help' }}>
                              <Badge tone="warning" style={{ height: 20, fontSize: 10.5 }}>⚠ out of {itemMax}, not {commonMax}</Badge>
                            </div>
                          )}
                        </td>
                        <td style={{ minWidth: 200 }}>
                          <div className="sl-ex-meter">
                            <div className="sl-ex-meter__track" style={{ height: 6 }}>
                              <div className="sl-ex-meter__fill" style={{ width: `${Math.max(2, Math.min(100, p ?? 0))}%`, background: band?.color ?? T.color.neutral[300] }} />
                            </div>
                            <span className="sl-badge sl-ex-pct" style={{ background: band?.bg ?? T.color.neutral[100], color: band?.text ?? T.text.tertiary, fontSize: 12 }}>{formatPercent(p)}</span>
                          </div>
                        </td>
                        <td>
                          {item.grade
                            ? <span className="sl-ex-grade sl-num">{item.grade}</span>
                            : <span style={{ color: T.text.tertiary }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Button variant="primary" size="sm" onClick={() => openStudentAnalytics(item)}
                            iconRight={<svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>}>
                            View Analytics
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
          </>
          );
        })()}
      </div>
    );
  }


  /* ─── Empty state ────────────────────────────────── */
  if (exams.length === 0) {
    return (
      <div style={{ fontFamily: FONT }}>
        <HeroCard
          pill="Exam correction workspace"
          title={<>Exam Overview</>}
          subtitle="Choose an exam to see student results and question-wise analysis."
          counterLabel="TOTAL EXAMS"
          counterValue="0"
          showArt
        />
        <CardShell><EmptyState title="No corrected exams yet" body="Once exams are uploaded and processed, they'll appear here so you can review student performance and question-level gaps." /></CardShell>
      </div>
    );
  }

  /* ─── Overview ─── */
  return (
    <div style={{ display: 'grid', gap: 20, fontFamily: FONT }}>

      <InsightHero
        eyebrow="Exam correction workspace"
        title="Exam Overview"
        insight="Choose an exam to see student results and question-wise analysis."
        stats={[{ label: 'Total exams', value: String(data?.total_exams ?? exams.length) }]}
      />

      {/* Toolbar */}
      <Card padding="12px 14px 12px 18px">
        <div className="sl-ex-toolbar">
          <div className="sl-ex-toolbar__count" aria-live="polite">
            Showing <b>{filteredExams.length}</b> of <span className="sl-num">{exams.length}</span> exams
          </div>
          <div className="sl-ex-toolbar__controls">
            {classOptions.length > 1 && (
              <FilterSelect value={classFilter} onChange={setClassFilter}
                options={[{ value: 'all', label: 'All classes' }, ...classOptions.map(([k, l]) => ({ value: k, label: l }))]} />
            )}
            {subjectOptions.length > 1 && (
              <FilterSelect value={subjectFilter} onChange={setSubjectFilter}
                options={[{ value: 'all', label: 'All subjects' }, ...subjectOptions.map(([k, l]) => ({ value: k, label: l }))]} />
            )}
            <FilterSelect value={sortBy} onChange={(v) => setSortBy(v as any)} neutral
              options={[
                { value: 'newest', label: 'Sort: Newest first' },
                { value: 'oldest', label: 'Sort: Oldest first' },
                { value: 'high-score', label: 'Sort: Highest score' },
                { value: 'low-score', label: 'Sort: Lowest score' },
                { value: 'most-students', label: 'Sort: Most students' },
                { value: 'attention', label: 'Sort: Needs attention' },
              ]} />
            <div className="sl-ex-search">
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
              </svg>
              <input
                type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search exams…"
                aria-label="Search exams"
                className="sl-input"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Exam grid */}
      <div className="sl-ex-grid">
        {filteredExams.map((exam, idx) => {
          const name = exam.name || exam.exam_name || `Exam #${exam.exam_id}`;
          const rec = recordById.get(exam.exam_id);
          const pending = isPending(exam);
          const avgScore = pending ? null : rec ? recordAvg(rec) : exam.average_score == null ? null : Number(exam.average_score);
          const type = exam.exam_type || 'Exam';
          const stats = rec?.stats && rec.stats.n > 0 ? rec.stats : null;
          const studentCount = stats?.n ?? exam.total_students ?? 0;

          return (
            <article key={exam.exam_id} className="sl-ex-card" aria-label={name}
              style={{ animation: `sl-fade-in-up 360ms ${T.motion.ease.spring} ${Math.min(idx, 12) * 35}ms both` }}>
              <header className="sl-ex-card__head">
                <IconBadge tone="brand" size={42}>
                  <svg width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.9" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14 2v6h6" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="8" y1="13" x2="16" y2="13" strokeLinecap="round" />
                    <line x1="8" y1="17" x2="14" y2="17" strokeLinecap="round" />
                  </svg>
                </IconBadge>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="sl-ex-card__title-row">
                    <h3 className="sl-ex-card__title">{name}</h3>
                    <Badge className="sl-num" style={{ flexShrink: 0 }}>#{exam.exam_id}</Badge>
                  </div>
                  <div className="sl-ex-card__chips">
                    <Badge tone="brand">{type}</Badge>
                    {rec && rec.classKey !== 'unknown' && <Badge>{rec.classLabel}</Badge>}
                    {rec && <Badge>{rec.subjectLabel}</Badge>}
                    {rec?.supersededBy && (
                      <Badge tone="warning" style={{ cursor: 'help' }}
                        title={`A newer upload of this ${rec.typeLabel} (${recordById.get(rec.supersededBy)?.name ?? ''}) is used in Analytics.`}>
                        Older upload
                      </Badge>
                    )}
                  </div>
                </div>
              </header>

              <div className="sl-ex-card__body">

                {pending ? (
                  <div className="sl-ex-pending">
                    <div className="sl-ex-pending__title">
                      <span className="sl-ex-pending__dot" aria-hidden />
                      Awaiting results
                    </div>
                    <div className="sl-ex-pending__body">Answer sheets haven't been processed yet. Results will appear here automatically.</div>
                  </div>
                ) : (
                  <>
                    {/* Two mini-stat cards */}
                    <div className="sl-ex-tiles">
                      <div className="sl-ex-tile">
                        <div className="sl-ex-tile__head">
                          <IconBadge tone="brand" size={26}><svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" strokeLinecap="round" /><circle cx="9" cy="7" r="4" /></svg></IconBadge>
                          <span className="sl-eyebrow" style={{ fontSize: 10.5 }}>Total Students</span>
                        </div>
                        <div className="sl-ex-tile__value">{typeof studentCount === 'number' ? <CountUp value={studentCount} /> : studentCount}</div>
                      </div>
                      <div className="sl-ex-tile">
                        <div className="sl-ex-tile__head">
                          <IconBadge tone="info" size={26}><svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24"><path d="M3 17l6-6 4 4 8-8" strokeLinecap="round" strokeLinejoin="round" /></svg></IconBadge>
                          <span className="sl-eyebrow" style={{ fontSize: 10.5 }}>Average Score</span>
                        </div>
                        <div className="sl-ex-tile__value sl-ex-tile__value--brand">{formatPercent(avgScore)}</div>
                      </div>
                    </div>

                    {/* Pass rate + score distribution */}
                    {stats && (
                      <div className="sl-ex-dist">
                        <div className="sl-ex-dist__head">
                          <span className="sl-ex-dist__label">
                            Pass rate <b>{formatPercent(stats.passRate).replace('.0%', '%')}</b>
                          </span>
                          {stats.bands.support > 0 ? (
                            <Badge tone="danger" dot>{stats.bands.support} need support</Badge>
                          ) : (
                            <Badge tone="success">✓ Everyone passed</Badge>
                          )}
                        </div>
                        <BandMiniChart bands={stats.bands} total={stats.n} />
                      </div>
                    )}
                  </>
                )}

                {/* Processed at */}
                <div className="sl-ex-meta">
                  <span>
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" strokeLinecap="round" /><line x1="8" y1="2" x2="8" y2="6" strokeLinecap="round" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                    Processed At
                  </span>
                  <strong>{formatDateTime(exam.processed_at)}</strong>
                </div>

                {/* View Analytics button */}
                <Button variant={pending ? 'secondary' : 'primary'} size="lg" block onClick={() => openExamAnalytics(exam)}
                  iconRight={<svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>}>
                  View Analytics
                </Button>
              </div>
            </article>
          );
        })}
      </div>

      {filteredExams.length === 0 && (
        <CardShell><EmptyState title="No exams match your search" body="Try a different keyword or clear the search to see all exams." /></CardShell>
      )}
    </div>
  );
};

/* ─── Small helpers ───────────────────────────────── */

/** Score distribution for an exam card — one bar per performance band, with its % range underneath. */
const BandMiniChart: React.FC<{ bands: Record<BandKey, number>; total: number }> = ({ bands, total }) => {
  const data = BANDS.map((b) => ({ key: b.key, label: b.label, range: b.range, n: bands[b.key], color: b.color }));
  return (
    <div style={{ height: 156, marginTop: 8 }} role="img"
      aria-label={`Score distribution: ${data.map((d) => `${d.label} ${d.n}`).join(', ')}`}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 20, right: 0, bottom: 0, left: 0 }} barCategoryGap="24%">
          <BandGradients />
          <XAxis dataKey="label" axisLine={{ stroke: '#E2E6EE' }} tickLine={false} interval={0} height={36}
            tick={(props: any) => {
              const { x, y, payload } = props;
              const b = data.find((d) => d.label === payload.value);
              return (
                <g transform={`translate(${x},${y})`}>
                  <text textAnchor="middle" dy={12} fontSize={10.5} fontWeight={700} fill={T.text.secondary} fontFamily={FONT}>{payload.value}</text>
                  <text textAnchor="middle" dy={25} fontSize={10} fontWeight={600} fill={T.text.tertiary} fontFamily={FONT}>{b?.range}</text>
                </g>
              );
            }} />
          <YAxis hide domain={[0, 'dataMax']} />
          <Tooltip cursor={chartCursorBar} wrapperStyle={{ outline: 'none', zIndex: 30 }}
            content={(props: any) => (
              <ChartTooltip {...props}
                title={(_, payload) => `${payload[0]?.payload?.label} (${payload[0]?.payload?.range})`}
                format={(v) => `${v} student${v === 1 ? '' : 's'} · ${total ? ((Number(v) / total) * 100).toFixed(0) : 0}%`} />
            )} />
          <Bar dataKey="n" name="Students" radius={[8, 8, 3, 3]} maxBarSize={40} {...seriesAnimation()}
            background={{ fill: '#EFF1F7', radius: 8 } as any}>
            {data.map((d) => <Cell key={d.key} fill={bandFill(d.key as BandKey)} />)}
            <LabelList dataKey="n" position="top" offset={6} style={{ fontSize: 11.5, fontWeight: 800, fill: T.text.primary, fontFamily: FONT, fontVariantNumeric: 'tabular-nums' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const FilterSelect: React.FC<{ value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; neutral?: boolean }> = ({ value, onChange, options, neutral }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={`sl-select${neutral || value === 'all' ? '' : ' is-filtered'}`}
    aria-label={options[0]?.label.startsWith('Sort') ? 'Sort exams' : options[0]?.label ?? 'Filter'}
  >
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const CardShell: React.FC<{ children: React.ReactNode; noPadding?: boolean; style?: React.CSSProperties }> = ({ children, noPadding, style }) => (
  <div className="sl-card" style={{
    overflow: 'hidden',
    padding: noPadding ? 0 : '22px 24px',
    ...style,
  }}>
    {children}
  </div>
);

/** Shared results table shell — sticky header, hover rows, tabular numbers. */
const ResultsTable: React.FC<{
  columns: string[];
  rightAlignLast?: boolean;
  children: React.ReactNode;
}> = ({ columns, rightAlignLast, children }) => (
  <div className="sl-card sl-ex-table-card">
    <div className="sl-table-wrap" style={{ borderTop: 0 }}>
      <table className="sl-table">
        <thead>
          <tr>
            {columns.map((label, i) => (
              <th key={label || `c${i}`} style={{ textAlign: rightAlignLast && i === columns.length - 1 ? 'right' : 'left' }}>{label || ''}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  </div>
);

const EmptyState: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <UiEmptyState icon="file" title={title} body={body} />
);

export default ExamCorrectionPanel;
